/**
 * Production vs development workflow catalogue (COMPANY_MAILBOX_AND_PROD_VIEW_PLAN.md Part 3).
 *
 * The backend decides the view (always 'prod' where WORKFLOW_VIEW_TOGGLE is off);
 * `toggle` is true where the switch is on (dev and prod), and users switch per account.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getSuiteForType } from '../types/suites';

export type WorkflowView = 'prod' | 'dev';

interface WorkflowViewState {
  view: WorkflowView;
  toggle: boolean;
  setView: (view: WorkflowView) => Promise<void>;
  /** In the production view a suite's individual tool opens as the suite's auto tool. */
  launchableType: (workflowType: string) => string;
}

const WorkflowViewContext = createContext<WorkflowViewState>({
  view: 'prod',
  toggle: false,
  setView: async () => undefined,
  launchableType: (t) => t,
});

/** Fired after the view changes so tool lists can reload. */
export const WORKFLOW_VIEW_CHANGED_EVENT = 'riskninja:workflow-view-changed';

const apiBase = () => process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export const WorkflowViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [view, setViewState] = useState<WorkflowView>('prod');
  const [toggle, setToggle] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${apiBase()}/api/workflows/view`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setViewState(data.view === 'dev' ? 'dev' : 'prod');
        setToggle(!!data.toggle);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setView = useCallback(
    async (next: WorkflowView) => {
      const res = await fetch(`${apiBase()}/api/workflows/view`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ view: next }),
      });
      if (!res.ok) throw new Error('Could not change the workflow view');
      setViewState(next);
      window.dispatchEvent(new CustomEvent(WORKFLOW_VIEW_CHANGED_EVENT, { detail: next }));
    },
    [token]
  );

  const launchableType = useCallback(
    (workflowType: string) => {
      if (view !== 'prod') return workflowType;
      const suite = getSuiteForType(workflowType);
      return suite ? suite.autoType : workflowType;
    },
    [view]
  );

  return <WorkflowViewContext.Provider value={{ view, toggle, setView, launchableType }}>{children}</WorkflowViewContext.Provider>;
};

export const useWorkflowView = () => useContext(WorkflowViewContext);
